import { FileText } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { getUnauthenticatedEntityData } from "@/utils/localStorageEntityData";
import { convertRonToEur } from "@/utils/currency-utils";

export function ReportPreviewCard() {
  const storedData = getUnauthenticatedEntityData();
  const entityData = storedData?.entityData;

  if (entityData) {
    const numeEntitate = entityData.nume_entitate || "N/A";
    const cui = entityData.cui || "N/A";
    const nrRegCom = entityData.anaf_raw_data?.date_generale?.nrRegCom || "N/A";
    const adresa = entityData.adresa || "N/A";
    const caenPrincipal = entityData.caen_principal?.cod || "N/A";
    const nrSalariati = entityData.nr_salariati ?? "N/A";
    const cifraAfaceri = entityData.cifra_afaceri;
    const active = entityData.active;
    const dimensiune = entityData.dimensiune || "N/A";

    return (
      <Card className="border-2 border-gray-300 bg-white shadow-lg overflow-hidden">
        <CardHeader className="bg-blue-900 text-white px-4 py-3">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <h4 className="text-sm font-bold uppercase tracking-wide">
              Raport formular identificare NIS2
            </h4>
          </div>
          <p className="text-xs text-blue-200 mt-1">
            Previzualizare date entitate
          </p>
        </CardHeader>
        <CardContent className="p-0 text-left">
          <div className="bg-blue-200 text-blue-900 px-4 py-1">
            <h5 className="text-xs font-semibold uppercase">
              Date privind identificarea entității
            </h5>
          </div>
          <div className="px-4 py-2 space-y-1">
            <p className="text-xs text-gray-700">
              <span className="text-gray-600">DENUMIRE ENTITATE:</span>{" "}
              <span className="font-medium text-gray-900">{numeEntitate}</span>
            </p>
            <p className="text-xs text-gray-700">
              <span className="text-gray-600">
                Codul unic de identificare (CUI):
              </span>{" "}
              <span className="font-medium text-gray-900 font-mono">{cui}</span>
            </p>
            <p className="text-xs text-gray-700">
              <span className="text-gray-600">
                Nr. de înregistrare în registre:
              </span>{" "}
              <span className="font-medium text-gray-900 font-mono">
                {nrRegCom}
              </span>
            </p>
          </div>

          <div className="bg-blue-200 text-blue-900 px-4 py-1">
            <h5 className="text-xs font-semibold uppercase">
              Date de contact ale entității
            </h5>
          </div>
          <div className="px-4 py-2 space-y-1">
            <p className="text-xs text-gray-700">
              <span className="text-gray-600">Adresă sediu social:</span>{" "}
              <span className="font-medium text-gray-900">{adresa}</span>
            </p>
            <p className="text-xs text-gray-500">...</p>
          </div>

          <div className="bg-blue-200 text-blue-900 px-4 py-1">
            <h5 className="text-xs font-semibold uppercase">
              Date privind activitatea desfășurată de către entitate
            </h5>
          </div>
          <div className="px-4 py-2 space-y-1">
            <p className="text-xs text-gray-700">
              <span className="text-gray-600">CAEN principal:</span>{" "}
              <span className="font-medium text-gray-900">{caenPrincipal}</span>
            </p>
            <p className="text-xs text-gray-500">...</p>
          </div>

          <div className="bg-blue-200 text-blue-900 px-4 py-1">
            <h5 className="text-xs font-semibold uppercase">
              Date privind dimensiunea entității
            </h5>
          </div>
          <div className="px-4 py-2 space-y-1">
            <p className="text-xs text-gray-700">
              <span className="text-gray-600">
                Numărul mediu anual de salariați:
              </span>{" "}
              <span className="font-medium text-gray-900">{nrSalariati}</span>
            </p>
            <p className="text-xs text-gray-700">
              <span className="text-gray-600">
                Cifra de afaceri anuală netă (EUR):
              </span>{" "}
              <span className="font-medium text-gray-900">
                {cifraAfaceri && cifraAfaceri > 0
                  ? new Intl.NumberFormat("ro-RO").format(
                      convertRonToEur(cifraAfaceri)
                    )
                  : "N/A"}
              </span>
            </p>
            <p className="text-xs text-gray-700">
              <span className="text-gray-600">
                Activele totale deținute (EUR):
              </span>{" "}
              <span className="font-medium text-gray-900">
                {active && active > 0
                  ? new Intl.NumberFormat("ro-RO").format(
                      convertRonToEur(active)
                    )
                  : "N/A"}
              </span>
            </p>
            <p className="text-xs text-gray-700">
              <span className="text-gray-600">
                Dimensiunea entității conform Legii nr. 346/2004:
              </span>{" "}
              <span className="font-medium text-gray-900">{dimensiune}</span>
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <style>{`
        .auth-vp-root { display: flex; flex-direction: column; gap: 32px; }
        .auth-vp-header { display: flex; flex-direction: column; gap: 16px; }
        .auth-vp-benefits { display: flex; flex-direction: column; gap: 16px; }
        .auth-vp-benefit { display: flex; gap: 16px; padding: 20px; border: 1px solid var(--ds-border-800); border-radius: 12px; background: rgba(255, 255, 255, 0.03); }
        .auth-vp-stat { white-space: nowrap; min-width: 100px; padding-top: 2px; }
        .auth-vp-benefit-body { display: flex; flex-direction: column; gap: 4px; }
        .auth-vp-footer { display: flex; flex-direction: column; gap: 8px; margin-top: 0; }
      `}</style>

      <div className="auth-vp-root">
        <div className="auth-vp-header">
          <h2 className="font-heading text-[32px] font-bold text-white leading-[1.2]">
            Conformitatea NIS2, simplificata.
          </h2>
          <p className="font-heading text-sm text-ds-blue-200 leading-[1.6]">
            Platforma care te ghideaza prin toate cerintele OUG 155/2024 — de la
            evaluare la documente oficiale DNSC.
          </p>
        </div>

        <div className="auth-vp-benefits">
          <div className="auth-vp-benefit">
            <div className="auth-vp-stat font-heading text-lg font-bold text-ds-blue-400">
              ~39 min
            </div>
            <div className="auth-vp-benefit-body">
              <p className="font-heading text-sm font-semibold text-white">
                Evaluare completa in sub 40 de minute
              </p>
              <p className="font-heading text-ds-xsm text-ds-text-400 leading-[1.4]">
                Parcurge toate fluxurile NIS2 intr-o singura sesiune ghidata.
              </p>
            </div>
          </div>

          <div className="auth-vp-benefit">
            <div className="auth-vp-stat font-heading text-lg font-bold text-ds-blue-400">
              Zero erori
            </div>
            <div className="auth-vp-benefit-body">
              <p className="font-heading text-sm font-semibold text-white">
                Calcule DNSC 100% automatizate
              </p>
              <p className="font-heading text-ds-xsm text-ds-text-400 leading-[1.4]">
                Corelari CAEN, clasificare risc ENIRE@RO si scoruri CyFun — fara
                interventie manuala.
              </p>
            </div>
          </div>

          <div className="auth-vp-benefit">
            <div className="auth-vp-stat font-heading text-lg font-bold text-ds-blue-400">
              4 doc.
            </div>
            <div className="auth-vp-benefit-body">
              <p className="font-heading text-sm font-semibold text-white">
                Documentatie gata de transmis la DNSC
              </p>
              <p className="font-heading text-ds-xsm text-ds-text-400 leading-[1.4]">
                Formular Notificare, Raport Risc, EVAL_MMS CyFun si Plan
                Remediere — format oficial.
              </p>
            </div>
          </div>
        </div>

        <div className="auth-vp-footer">
          <div className="font-heading text-xs text-ds-text-400 flex flex-wrap items-center gap-2">
            <span>OUG 155/2024</span>
            <span>·</span>
            <span>Metodologie DNSC</span>
            <span>·</span>
            <span>Cadru CyFun</span>
          </div>
          <p className="font-heading text-xs text-ds-text-400">
            425+ rapoarte DNSC generate
          </p>
          <p className="font-heading text-xs text-ds-blue-400">
            Cont gratuit · 100 credite incluse
          </p>
        </div>
      </div>
    </>
  );
}
